// @file Surface reactions store
import appCan from '@@/bits/app_can'
import device from '@@/bits/device'
import { isEmojiReactionData, isNonEmojiReactionData, RED_HEART_EMOJI, THUMBS_UP_EMOJI } from '@@/bits/emoji'
import { captureFetchException, captureNonNetworkFetchError } from '@@/bits/error_tracker'
import { isAppUsing } from '@@/bits/flip'
import { getVuexStore } from '@@/bits/pinia'
import { vSet } from '@@/bits/vue'
import { HttpCode } from '@@/enums'
import { openPostReactionDialog } from '@@/native_bridge/actions'
import postMessage from '@@/native_bridge/post_message'
import { useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { useNativeAppStore } from '@@/pinia/native_app'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceCurrentUserStore } from '@@/pinia/surface_current_user'
import type { ContributionType } from '@@/pinia/surface_guest_store'
import { useSurfaceGuestStore } from '@@/pinia/surface_guest_store'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useSurfaceSettingsStore } from '@@/pinia/surface_settings'
import { useSurfaceUserContributorsStore } from '@@/pinia/surface_user_contributors'
import type { CreateOrUpdateReactionPayload, ReactionUser } from '@@/surface/api/reaction'
import { ReactionApi } from '@@/surface/api/reaction'
import type {
  AccumulatedEmojiReactionData,
  AccumulatedNonEmojiReactionData,
  AccumulatedReactionData,
  AccumulatedReactions,
  Id,
  Post,
  User,
  WallReactionData,
} from '@@/types'
import { useDelayedReactionSorting } from '@@/vuecomposables/useDelayedReactionSorting'
import type { RootState as SurfaceState } from '@@/vuexstore/surface/types'
import { ReactionType, type JsonAPIResource, type Reaction } from '@padlet/arvo'
import { sortBy } from 'es-toolkit'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

interface BaseRealtimeReactionPayload {
  wishId: number
  userHashid: string
  totalReactionCountChange: number
  reactionType: ReactionType
}

interface RealtimeEmojiReactionPayload extends BaseRealtimeReactionPayload {
  reactionType: ReactionType.Emoji
  // Create/delete fields
  emojiCode?: string
  // Update fields
  oldEmojiCode?: string
  newEmojiCode?: string
}

interface RealtimeNonEmojiReactionPayload extends BaseRealtimeReactionPayload {
  sumChange: number
}

type RealtimeReactionPayload = RealtimeEmojiReactionPayload | RealtimeNonEmojiReactionPayload

interface UsersWhoReactedData {
  users: ReactionUser[]
  nextCursor?: string
  hasMore: boolean
}

export type ReactionUserKey = `${Post['id']}-${Reaction['value']}`

const createReactionUser = (user: User): ReactionUser => {
  return {
    user_hashid: user.hashid ?? '',
    username: user.username ?? null,
    avatar: user.avatar ?? '',
    display_name: user.display_name ?? user.name ?? '',
  }
}

export const useReactionsStore = defineStore('reactions', () => {
  const surfaceVuexStore = getVuexStore<SurfaceState>()

  const globalSnackbarStore = useGlobalSnackbarStore()
  const surfaceSettingsStore = useSurfaceSettingsStore()
  const surfaceStore = useSurfaceStore()
  const surfaceGuestStore = useSurfaceGuestStore()
  const surfacePostsStore = useSurfacePostsStore()
  const surfaceUserContributorsStore = useSurfaceUserContributorsStore()
  const surfaceCurrentUserStore = useSurfaceCurrentUserStore()

  const originalReactionData = computed<WallReactionData>(
    () => surfaceVuexStore?.state.wall.reaction_data ?? { type: ReactionType.None },
  )
  const currentReactionData = computed<WallReactionData>(
    () => surfaceSettingsStore.previewAttributes.reaction_data ?? originalReactionData.value,
  )
  const currentReactionType = computed(() =>
    !surfaceSettingsStore.isReactable ? ReactionType.None : currentReactionData.value.type,
  )

  const allowedEmojis = computed<string[] | null>(() => {
    const reactionData = currentReactionData.value
    if (reactionData?.type === 'emoji' && reactionData?.allowed_emojis) {
      return Array.isArray(reactionData.allowed_emojis) && reactionData.allowed_emojis.length > 0
        ? reactionData.allowed_emojis.map((code) => code.toLowerCase())
        : null
    }
    return null
  })

  const shouldShowVoteStyleButton = computed(() => {
    const allowed = allowedEmojis.value
    if (!allowed || allowed.length !== 1) {
      return false
    }
    const allowedCode = allowed[0].toLowerCase()
    return allowedCode === THUMBS_UP_EMOJI.code.toLowerCase() || allowedCode === RED_HEART_EMOJI.code.toLowerCase()
  })

  const voteStyleEmojiCode = computed<string | null>(() => {
    if (!shouldShowVoteStyleButton.value || !allowedEmojis.value) {
      return null
    }
    return allowedEmojis.value[0].toLowerCase()
  })

  const shouldFallbackWsDataFetchToRest = computed<boolean>(
    () => surfaceVuexStore?.state.shouldFallbackWsDataFetchToRest || false,
  )

  /**
   * FETCH REACTIONS
   */
  const accumulatedReactionsByWishId = ref<AccumulatedReactions>({})
  const postIdBeingReactedTo = ref()
  const reactionDropdownTop = ref()
  const reactionDropdownLeft = ref()
  const reactionDropdownWidth = ref()
  // TODO: remove once reactionIdsByPostId props are cleaned up.
  const reactionIdsByPost = ref({})

  const {
    shouldDeferSortingPostsByReactions,
    accumulatedReactionsForDelayedSorting,
    startTrackingPostForDelayedReactionSort,
    stopTrackingPostForDelayedReactionSort,
  } = useDelayedReactionSorting(accumulatedReactionsByWishId)

  const hasReactions = computed<boolean>(() => {
    const wishesWithReactions = Object.keys(accumulatedReactionsByWishId.value)
    if (wishesWithReactions.length === 0) return false
    return wishesWithReactions.some((wishId) => accumulatedReactionsByWishId.value[wishId].totalReactionsCount > 0)
  })

  const fetchAccumulatedReactions = async (): Promise<void> => {
    if (surfaceStore.isSubmissionRequest) return

    try {
      let attributes
      if (!shouldFallbackWsDataFetchToRest.value && isAppUsing('realtimeFetching')) {
        attributes = await surfaceVuexStore?.dispatch('realtime/fetchAccumulatedReactions', null, { root: true })
      } else {
        const fetchedData = await ReactionApi.fetchAccumulatedReactions({ wallId: surfaceStore.wallId })
        const { data } = fetchedData
        if (data == null) return
        attributes = (data as JsonAPIResource<AccumulatedReactions>).attributes
      }

      accumulatedReactionsByWishId.value = attributes ?? {}
      void useNativeAppStore().postSurfaceState()
    } catch (error) {
      globalSnackbarStore.genericFetchError()
      if (error?.status !== HttpCode.Unauthorized)
        captureNonNetworkFetchError(error, { source: 'fetchAccumulatedReactions' })
    }
  }

  const accumulatedReactionsTotalSumAverage = computed(() => {
    const reactionWishIds = Object.keys(accumulatedReactionsByWishId.value)
    if (reactionWishIds.length === 0) return 0

    const sum = reactionWishIds.reduce((acc, wishId) => {
      const reaction = accumulatedReactionsByWishId.value[wishId]
      if (reaction?.reactionType === ReactionType.Emoji) {
        // Skip emoji reactions in sum calculation since they don't have a sum field
        return acc
      }
      const reactionSum: number = isNonEmojiReactionData(reaction) ? reaction.sum : 0
      return acc + reactionSum
    }, 0)

    const totalReactions = reactionWishIds.reduce((acc, wishId) => {
      const reactionsTotal: number = accumulatedReactionsByWishId.value[wishId]?.totalReactionsCount ?? 0
      return acc + reactionsTotal
    }, 0)

    return totalReactions > 0 ? sum / totalReactions : 0
  })

  const accumulatedReactionsConfidenceNumber = computed<number | undefined>(() => {
    // the totalReactions of the median reaction in all reactions for a wall
    // we choose median to be safe can always update if needed
    const reactionsList = Object.values(accumulatedReactionsByWishId.value).filter(
      (r): r is AccumulatedReactionData => r != null,
    )
    const numReactions = reactionsList.length
    const sortedReactionList = sortBy(reactionsList, ['totalReactionsCount'])
    return sortedReactionList[Math.floor(numReactions / 2)]?.totalReactionsCount
  })

  /** REALTIME HANDLERS */

  function updateAccumulatedReactions(payload: RealtimeReactionPayload): void {
    const { wishId, totalReactionCountChange, reactionType } = payload

    // Check if payload reaction type matches current wall reaction type
    // If not, this is a stale event from deleted reactions (wall reaction type changed)
    if (reactionType !== currentReactionType.value) {
      // Reset if stored reactions for this wish has wrong structure
      const existingReaction = accumulatedReactionsByWishId.value[wishId]
      if (existingReaction == null || existingReaction.reactionType !== currentReactionType.value) {
        delete accumulatedReactionsByWishId.value[wishId]
        void useNativeAppStore().postSurfaceState()
      }
      return
    }

    if (reactionType === ReactionType.Emoji) {
      const { emojiCode, oldEmojiCode, newEmojiCode } = payload as RealtimeEmojiReactionPayload
      const currentAccumulatedReactions: AccumulatedEmojiReactionData = (accumulatedReactionsByWishId.value?.[
        wishId
      ] as AccumulatedEmojiReactionData) ?? {
        emojis: {},
        totalReactionsCount: 0,
        reactionType: ReactionType.Emoji,
      }

      const currentEmojis = currentAccumulatedReactions.emojis ?? {}
      const newEmojis = { ...currentEmojis }

      if (oldEmojiCode && newEmojiCode) {
        if (oldEmojiCode !== newEmojiCode) {
          // Decrement old emoji count
          const oldCount = (newEmojis[oldEmojiCode] ?? 1) - 1
          if (oldCount > 0) {
            newEmojis[oldEmojiCode] = oldCount
          } else {
            delete newEmojis[oldEmojiCode]
          }

          // Increment new emoji count
          newEmojis[newEmojiCode] = (newEmojis[newEmojiCode] ?? 0) + 1
        }
      } else if (emojiCode) {
        // Handle emoji create/delete
        const newCount = (newEmojis[emojiCode] ?? 0) + totalReactionCountChange
        if (newCount > 0) {
          newEmojis[emojiCode] = newCount
        } else {
          delete newEmojis[emojiCode]
        }
      }

      vSet(accumulatedReactionsByWishId.value, wishId, {
        ...currentAccumulatedReactions,
        emojis: newEmojis,
        totalReactionsCount: currentAccumulatedReactions.totalReactionsCount + totalReactionCountChange,
        reactionType,
      })

      void updateUsersWhoReactedFromRealtime(payload)
    } else {
      // For non-emoji reactions, use the sum field
      const currentAccumulatedReactions: AccumulatedNonEmojiReactionData = (accumulatedReactionsByWishId.value?.[
        wishId
      ] as AccumulatedNonEmojiReactionData) ?? {
        sum: 0,
        totalReactionsCount: 0,
        reactionType: originalReactionData.value.type as Exclude<ReactionType, ReactionType.Emoji>,
      }

      const { sumChange } = payload
      vSet(accumulatedReactionsByWishId.value, wishId, {
        ...currentAccumulatedReactions,
        sum: currentAccumulatedReactions.sum + (sumChange ?? 0),
        totalReactionsCount: currentAccumulatedReactions.totalReactionsCount + totalReactionCountChange,
        reactionType,
      })
    }

    void useNativeAppStore().postSurfaceState()
  }

  function updateUserReaction(payload: Partial<Reaction>): void {
    const wishId = payload.wish_id
    if (wishId == null) return

    const reactionType = payload.reaction_type ?? originalReactionData.value.type

    if (reactionType === ReactionType.Emoji) {
      const currentAccumulatedReactions = (accumulatedReactionsByWishId.value?.[wishId] ?? {
        emojis: {},
        totalReactionsCount: 0,
        reactionType: originalReactionData.value.type,
      }) as AccumulatedEmojiReactionData
      const newAccumulatedReactions: AccumulatedEmojiReactionData = {
        ...currentAccumulatedReactions,
        userReaction: payload.id != null ? (payload as Reaction & { value: string }) : undefined,
        reactionType,
      }
      vSet(accumulatedReactionsByWishId.value, wishId, newAccumulatedReactions)
    } else {
      const currentAccumulatedReactions = (accumulatedReactionsByWishId.value?.[wishId] ?? {
        sum: 0,
        totalReactionsCount: 0,
        reactionType: originalReactionData.value.type,
      }) as AccumulatedNonEmojiReactionData
      const newAccumulatedReactions: AccumulatedNonEmojiReactionData = {
        ...currentAccumulatedReactions,
        userReaction: payload.id != null ? (payload as Reaction & { value: number }) : undefined,
      }
      vSet(accumulatedReactionsByWishId.value, wishId, newAccumulatedReactions)
    }

    void useNativeAppStore().postSurfaceState()
  }

  function refreshAccumulatedReactions(newAccumulatedReactionsByWishId: AccumulatedReactions): void {
    accumulatedReactionsByWishId.value = newAccumulatedReactionsByWishId
    void useNativeAppStore().postSurfaceState()
  }

  /** ACTIONS */

  const isUpdatingReaction = ref<boolean>(false)

  const createReaction = async (payload: CreateOrUpdateReactionPayload): Promise<void> => {
    if (surfaceGuestStore.shouldShowGuestIdModal) {
      surfaceGuestStore.showGuestIdModal({
        afterSaveActions: [async () => await createReaction(payload)],
        contributionType: originalReactionData.value.type as Exclude<ReactionType, ReactionType.None> &
          Exclude<ContributionType, ContributionType.DraftPost | ContributionType.Comment>,
        contributionPayload: payload,
      })
      return
    } else if (surfaceGuestStore.shouldUpdateNameBeforePublishing) {
      surfaceGuestStore.setUserName(surfaceGuestStore.displayName)
      await surfaceGuestStore.updateSessionUserName({ shouldModerate: false })
    }

    const { postId, value } = payload

    const post = surfacePostsStore.postEntitiesById[postId]
    if (post == null) {
      void globalSnackbarStore.genericFetchError()
      return
    }

    if (isUpdatingReaction.value) return
    isUpdatingReaction.value = true

    // Save the old state for revert in case of error
    let oldAccumulatedReactions: AccumulatedEmojiReactionData | AccumulatedNonEmojiReactionData
    const newReaction = {
      wish_id: postId,
      value,
      reaction_type: originalReactionData.value.type,
    }

    // Save old state before optimistic change
    const hasReactionTypeChanged =
      (accumulatedReactionsByWishId.value[postId]?.reactionType ?? originalReactionData.value.type) !==
      newReaction.reaction_type

    if (newReaction.reaction_type === ReactionType.Emoji) {
      oldAccumulatedReactions = (accumulatedReactionsByWishId.value[postId] ?? {
        emojis: {},
        totalReactionsCount: 0,
        reactionType: originalReactionData.value.type,
      }) as AccumulatedEmojiReactionData

      // Mark post as being hovered after reaction BEFORE the optimistic update if we should defer sorting
      if (shouldDeferSortingPostsByReactions.value) {
        startTrackingPostForDelayedReactionSort(postId, oldAccumulatedReactions)
      }

      const currentEmojis = oldAccumulatedReactions.emojis ?? {}
      const emojiKey = newReaction.value.toString()
      const newEmojis = {
        ...currentEmojis,
        [emojiKey]: (currentEmojis[emojiKey] ?? 0) + 1,
      }

      const newAccumulatedReactions: AccumulatedEmojiReactionData = {
        emojis: newEmojis,
        totalReactionsCount: 1 + (hasReactionTypeChanged ? 0 : oldAccumulatedReactions.totalReactionsCount),
        reactionType: newReaction.reaction_type,
        userReaction: newReaction as Reaction & { value: string },
      }
      vSet(accumulatedReactionsByWishId.value, postId, newAccumulatedReactions)
    } else {
      oldAccumulatedReactions = (accumulatedReactionsByWishId.value[postId] ?? {
        sum: 0,
        totalReactionsCount: 0,
        reactionType: originalReactionData.value.type,
      }) as AccumulatedNonEmojiReactionData

      // Mark post as being hovered after reaction BEFORE the optimistic update if we should defer sorting
      if (shouldDeferSortingPostsByReactions.value) {
        startTrackingPostForDelayedReactionSort(postId, oldAccumulatedReactions)
      }

      const newAccumulatedReactions: AccumulatedNonEmojiReactionData = {
        sum: (newReaction.value as number) + (hasReactionTypeChanged ? 0 : oldAccumulatedReactions.sum),
        totalReactionsCount: 1 + (hasReactionTypeChanged ? 0 : oldAccumulatedReactions.totalReactionsCount),
        reactionType: newReaction.reaction_type,
        userReaction: newReaction as Reaction & { value: number },
      }
      vSet(accumulatedReactionsByWishId.value, postId, newAccumulatedReactions)
    }

    try {
      const reactionData = await ReactionApi.create(newReaction)
      const resultingReaction = (reactionData?.data as JsonAPIResource<Reaction>).attributes

      // Update with the resulting reaction from the server
      if (newReaction.reaction_type === ReactionType.Emoji) {
        const currentReactions = accumulatedReactionsByWishId.value[postId]
        if (currentReactions && isEmojiReactionData(currentReactions)) {
          const newAccumulatedReactions: AccumulatedEmojiReactionData = {
            ...currentReactions,
            userReaction: resultingReaction as Reaction & { value: string },
          }
          vSet(accumulatedReactionsByWishId.value, postId, newAccumulatedReactions)
        }
      } else {
        const currentReactions = accumulatedReactionsByWishId.value[postId]
        if (currentReactions && isNonEmojiReactionData(currentReactions)) {
          const newAccumulatedReactions: AccumulatedNonEmojiReactionData = {
            ...currentReactions,
            userReaction: resultingReaction as Reaction & { value: number },
          }
          vSet(accumulatedReactionsByWishId.value, postId, newAccumulatedReactions)
        }
      }

      addUserToEmoji(
        createReactionUserKey(postId, newReaction.value),
        createReactionUser(surfaceCurrentUserStore.currentUser),
      )
    } catch (error) {
      // Revert optimistic change (might come in handy in the event that user has no internet connection)
      vSet(accumulatedReactionsByWishId.value, postId, oldAccumulatedReactions)
      // Refetch reactions to get the latest state
      void fetchAccumulatedReactions()

      globalSnackbarStore.genericFetchError()
      // Don't send 401 error since this is likely due to the owner changing the permissions
      // of the wall such that the user is not allowed to add/delete reactions (i.e. expected behavior)
      if (error?.status === HttpCode.Unauthorized) return
      captureNonNetworkFetchError(error, { source: 'createReaction' })
    } finally {
      isUpdatingReaction.value = false
    }
  }

  const updateReaction = async (payload: CreateOrUpdateReactionPayload): Promise<void> => {
    if (isUpdatingReaction.value) return

    isUpdatingReaction.value = true
    const { postId, value } = payload
    const originalAccumulatedReactions = accumulatedReactionsByWishId.value[postId]
    const userReaction = originalAccumulatedReactions?.userReaction
    if (
      originalAccumulatedReactions == null ||
      userReaction == null ||
      userReaction.value === value ||
      userReaction.reaction_type !== originalReactionData.value.type
    ) {
      isUpdatingReaction.value = false
      return
    }

    // Save old state before optimistic change
    const newReaction = {
      ...userReaction,
      value,
      reactionType: originalReactionData.value.type,
    }

    let oldAccumulatedReactions: AccumulatedEmojiReactionData | AccumulatedNonEmojiReactionData

    if (newReaction.reactionType === ReactionType.Emoji) {
      oldAccumulatedReactions = (accumulatedReactionsByWishId.value[postId] ?? {
        emojis: {},
        totalReactionsCount: 0,
        reactionType: originalReactionData.value.type,
      }) as AccumulatedEmojiReactionData

      // Mark post as being hovered after reaction BEFORE the optimistic update if we should defer sorting
      if (shouldDeferSortingPostsByReactions.value) {
        startTrackingPostForDelayedReactionSort(postId, oldAccumulatedReactions)
      }

      const currentEmojis = oldAccumulatedReactions.emojis
      const oldEmojiKey = userReaction.value.toString()
      const newEmojiKey = newReaction.value.toString()

      const newEmojis = { ...currentEmojis }
      if (oldEmojiKey !== newEmojiKey) {
        newEmojis[oldEmojiKey] = (newEmojis[oldEmojiKey] ?? 1) - 1
        if (newEmojis[oldEmojiKey] <= 0) {
          delete newEmojis[oldEmojiKey]
        }
        newEmojis[newEmojiKey] = (newEmojis[newEmojiKey] ?? 0) + 1
      }

      const newAccumulatedReactions: AccumulatedEmojiReactionData = {
        emojis: newEmojis,
        totalReactionsCount: oldAccumulatedReactions.totalReactionsCount,
        reactionType: newReaction.reactionType,
        userReaction: newReaction as Reaction & { value: string },
      }
      vSet(accumulatedReactionsByWishId.value, postId, newAccumulatedReactions)
    } else {
      oldAccumulatedReactions = (accumulatedReactionsByWishId.value[postId] ?? {
        sum: 0,
        totalReactionsCount: 0,
        reactionType: originalReactionData.value.type,
      }) as AccumulatedNonEmojiReactionData

      // Mark post as being hovered after reaction BEFORE the optimistic update if we should defer sorting
      if (shouldDeferSortingPostsByReactions.value) {
        startTrackingPostForDelayedReactionSort(postId, oldAccumulatedReactions)
      }

      const newAccumulatedReactions: AccumulatedNonEmojiReactionData = {
        sum: oldAccumulatedReactions.sum - (userReaction.value as number) + (newReaction.value as number),
        totalReactionsCount: oldAccumulatedReactions.totalReactionsCount,
        reactionType: newReaction.reactionType,
        userReaction: newReaction as Reaction & { value: number },
      }
      vSet(accumulatedReactionsByWishId.value, postId, newAccumulatedReactions)
    }

    const existingReactionId = userReaction.id
    try {
      await ReactionApi.update({ id: existingReactionId, value })

      removeUserFromEmoji(createReactionUserKey(postId, userReaction.value), surfaceCurrentUserStore.currentUser.hashid)
      addUserToEmoji(
        createReactionUserKey(postId, newReaction.value),
        createReactionUser(surfaceCurrentUserStore.currentUser),
      )
    } catch (error) {
      // Revert optimistic change (might come in handy in the event that user has no internet connection)
      vSet(accumulatedReactionsByWishId.value, postId, oldAccumulatedReactions)
      // Refetch reactions to get the latest state
      void fetchAccumulatedReactions()

      globalSnackbarStore.genericFetchError()
      // Don't send 422 error since its already handled by the refetch. This happens when
      // the reaction is created by the same user at the same time on another device which
      // causes a race condition.
      if (error?.status === HttpCode.UnprocessableEntity) return

      // Don't send 401 error since this is likely due to the owner changing the permissions
      // of the wall such that the user is not allowed to add/delete reactions (i.e. expected behavior)
      if (error?.status === HttpCode.Unauthorized) return
      captureNonNetworkFetchError(error, { source: 'updateReaction' })
    } finally {
      isUpdatingReaction.value = false
    }
  }

  const deleteReaction = async (payload: { postId: Id }): Promise<void> => {
    if (isUpdatingReaction.value) return

    isUpdatingReaction.value = true
    const { postId } = payload
    const originalAccumulatedReactions = accumulatedReactionsByWishId.value[postId]
    const userReaction = originalAccumulatedReactions?.userReaction
    if (
      originalAccumulatedReactions == null ||
      userReaction == null ||
      userReaction.reaction_type !== originalReactionData.value.type
    ) {
      return
    }

    let oldAccumulatedReactions: AccumulatedEmojiReactionData | AccumulatedNonEmojiReactionData
    // Save old state before optimistic change
    if (userReaction.reaction_type === ReactionType.Emoji) {
      oldAccumulatedReactions = (accumulatedReactionsByWishId.value[postId] ?? {
        emojis: {},
        totalReactionsCount: 0,
        reactionType: originalReactionData.value.type,
      }) as AccumulatedEmojiReactionData

      // Mark post as being hovered after reaction BEFORE the optimistic update if we should defer sorting
      if (shouldDeferSortingPostsByReactions.value) {
        startTrackingPostForDelayedReactionSort(postId, oldAccumulatedReactions)
      }

      const currentEmojis = oldAccumulatedReactions.emojis
      const emojiKey = userReaction.value.toString()

      const newEmojis = { ...currentEmojis }
      newEmojis[emojiKey] = (newEmojis[emojiKey] ?? 1) - 1
      if (newEmojis[emojiKey] <= 0) {
        delete newEmojis[emojiKey]
      }

      const newAccumulatedReactions: AccumulatedEmojiReactionData = {
        emojis: newEmojis,
        totalReactionsCount: oldAccumulatedReactions.totalReactionsCount - 1,
        reactionType: originalReactionData.value.type as ReactionType.Emoji,
        userReaction: undefined,
      }
      vSet(accumulatedReactionsByWishId.value, postId, newAccumulatedReactions)
    } else {
      oldAccumulatedReactions = (accumulatedReactionsByWishId.value[postId] ?? {
        sum: 0,
        totalReactionsCount: 0,
        reactionType: originalReactionData.value.type,
      }) as AccumulatedNonEmojiReactionData

      // Mark post as being hovered after reaction BEFORE the optimistic update if we should defer sorting
      if (shouldDeferSortingPostsByReactions.value) {
        startTrackingPostForDelayedReactionSort(postId, oldAccumulatedReactions)
      }

      const newAccumulatedReactions: AccumulatedNonEmojiReactionData = {
        sum: oldAccumulatedReactions.sum - (userReaction.value as number),
        totalReactionsCount: oldAccumulatedReactions.totalReactionsCount - 1,
        reactionType: originalReactionData.value.type as Exclude<ReactionType, ReactionType.Emoji>,
        userReaction: undefined,
      }
      vSet(accumulatedReactionsByWishId.value, postId, newAccumulatedReactions)
    }

    const existingReactionId = userReaction.id
    try {
      await ReactionApi.delete({ id: existingReactionId })
      removeUserFromEmoji(createReactionUserKey(postId, userReaction.value), surfaceCurrentUserStore.currentUser.hashid)
    } catch (error) {
      // Revert optimistic change (might come in handy in the event that user has no internet connection)
      vSet(accumulatedReactionsByWishId.value, postId, oldAccumulatedReactions)
      // Refetch reactions to get the latest state
      void fetchAccumulatedReactions()

      globalSnackbarStore.genericFetchError()
      // Don't send 404 error since its already handled by the refetch. This happens when
      // the reaction is deleted by the same user at the same time on another device which
      // causes a race condition.
      if (error?.status === HttpCode.NotFound) return

      // Don't send 401 error since this is likely due to the owner changing the permissions
      // of the wall such that the user is not allowed to add/delete reactions (i.e. expected behavior)
      if (error?.status === HttpCode.Unauthorized) return
      captureNonNetworkFetchError(error, { source: 'deleteReaction' })
    } finally {
      isUpdatingReaction.value = false
    }
  }

  const startEditingReaction = async (payload: {
    postId: Id
    dropdownLeft?: number
    dropdownWidth?: number
    dropdownTop?: number
  }): Promise<void> => {
    if (device.app && appCan('useNativeReactionDialog')) {
      // update surface state right before editing to make sure reaction data is up to date
      void useNativeAppStore().postSurfaceState()
      postMessage(openPostReactionDialog(payload.postId))
    } else {
      postIdBeingReactedTo.value = payload.postId
      reactionDropdownLeft.value = payload.dropdownLeft ?? 0
      reactionDropdownWidth.value = payload.dropdownWidth ?? 0
      reactionDropdownTop.value = payload.dropdownTop ?? 0
    }
  }

  const stopEditingReaction = (): void => {
    postIdBeingReactedTo.value = undefined
    reactionDropdownLeft.value = 0
    reactionDropdownWidth.value = 0
    reactionDropdownTop.value = 0
  }

  // #region Users Who Reacted
  const usersWhoReactedByWishIdAndReactionValue = ref<Record<ReactionUserKey, UsersWhoReactedData>>({})
  const isLoadingUsersWhoReactedByIdAndReactionValue = ref<Record<ReactionUserKey, boolean>>({})

  const createReactionUserKey = (wishId: Id, value: string | number): ReactionUserKey => {
    return `${wishId}-${value}` as ReactionUserKey
  }

  const fetchUsersWhoReacted = async ({
    wishId,
    value,
    cursor,
  }: {
    wishId: Id
    value: string | number
    cursor?: string
  }): Promise<{ users: ReactionUser[]; hasMore: boolean; nextCursor?: string }> => {
    if (value == null || wishId == null) {
      return { users: [], hasMore: false }
    }

    // If no cursor provided, check if we already have data for this post and emoji
    const reactionKey = createReactionUserKey(wishId, value)
    if (cursor == null && usersWhoReactedByWishIdAndReactionValue.value[reactionKey] != null) {
      const existingData = usersWhoReactedByWishIdAndReactionValue.value[reactionKey]
      return {
        users: existingData.users,
        hasMore: existingData.hasMore,
        nextCursor: existingData.nextCursor,
      }
    }

    // Set loading state
    if (!isLoadingUsersWhoReactedByIdAndReactionValue.value[reactionKey]) {
      vSet(isLoadingUsersWhoReactedByIdAndReactionValue.value, reactionKey, false)
    }
    vSet(isLoadingUsersWhoReactedByIdAndReactionValue.value, reactionKey, true)

    try {
      const wishHashId = surfacePostsStore.postEntitiesById[wishId]?.hashid
      if (wishHashId == null) {
        return { users: [], hasMore: false }
      }
      const response = await ReactionApi.fetchUsersThatReactedToWish({
        wishHashId,
        value,
        cursor,
      })

      // If this is the first page (no cursor), store the fetched users
      // If it's a subsequent page, append to existing users
      if (cursor == null) {
        vSet(usersWhoReactedByWishIdAndReactionValue.value, reactionKey, {
          users: response.data,
          nextCursor: response.pagination.next_cursor ?? undefined,
          hasMore: response.pagination.has_more,
        })
      } else {
        const existingData = usersWhoReactedByWishIdAndReactionValue.value[reactionKey] ?? {
          users: [],
          hasMore: false,
        }
        vSet(usersWhoReactedByWishIdAndReactionValue.value, reactionKey, {
          users: [...existingData.users, ...response.data],
          nextCursor: response.pagination.next_cursor ?? undefined,
          hasMore: response.pagination.has_more,
        })
      }

      return {
        users: response.data,
        hasMore: response.pagination.has_more,
        nextCursor: response.pagination.next_cursor ?? undefined,
      }
    } catch (error) {
      captureFetchException(error, { source: 'fetchUsersWhoReacted' })
      // Initialize with empty data on error
      if (cursor == null) {
        vSet(usersWhoReactedByWishIdAndReactionValue.value, reactionKey, { users: [], hasMore: false })
      }
      return { users: [], hasMore: false }
    } finally {
      vSet(isLoadingUsersWhoReactedByIdAndReactionValue.value, reactionKey, false)
    }
  }

  const getUsersWhoReacted = ({ wishId, emojiCode }: { wishId: Id; emojiCode: string }): ReactionUser[] => {
    const reactionKey = createReactionUserKey(wishId, emojiCode)
    return usersWhoReactedByWishIdAndReactionValue.value[reactionKey]?.users ?? []
  }

  const fetchMoreUsersWhoReacted = async ({
    wishId,
    value,
  }: {
    wishId: Id
    value: string | number
  }): Promise<{ users: ReactionUser[]; hasMore: boolean; nextCursor?: string }> => {
    // Get the current data to determine the next cursor
    const reactionKey = createReactionUserKey(wishId, value)
    const currentData = usersWhoReactedByWishIdAndReactionValue.value[reactionKey]

    // If we have no data yet, fetch the first page
    if (currentData == null) {
      return await fetchUsersWhoReacted({ wishId, value })
    }

    // If we don't have more pages or no next cursor, return empty result
    if (!currentData.hasMore || !currentData.nextCursor) {
      return { users: [], hasMore: false }
    }

    // Fetch the next page using the cursor
    return await fetchUsersWhoReacted({
      wishId,
      value,
      cursor: currentData.nextCursor,
    })
  }

  const getIsLoadingUsers = (wishId: Id, value: string | number): boolean => {
    const reactionKey = createReactionUserKey(wishId, value)
    return isLoadingUsersWhoReactedByIdAndReactionValue.value[reactionKey] ?? false
  }

  const getBaseUsersWhoReactedData = (emojiKey: ReactionUserKey): UsersWhoReactedData => {
    return (
      usersWhoReactedByWishIdAndReactionValue.value[emojiKey] ?? {
        users: [],
        hasMore: false,
      }
    )
  }

  const removeUserFromEmoji = (emojiKey: ReactionUserKey, userHashid: string): void => {
    const emojiData = getBaseUsersWhoReactedData(emojiKey)
    const existingData = usersWhoReactedByWishIdAndReactionValue.value[emojiKey]
    if (existingData != null) {
      vSet(usersWhoReactedByWishIdAndReactionValue.value, emojiKey, {
        ...emojiData,
        users: emojiData.users.filter((user) => user.user_hashid !== userHashid),
      })
    }
  }

  const addUserToEmoji = (emojiKey: ReactionUserKey, reactionUser: ReactionUser): void => {
    const emojiData = getBaseUsersWhoReactedData(emojiKey)
    const existingData = usersWhoReactedByWishIdAndReactionValue.value[emojiKey]
    if (existingData != null) {
      // We have cached data, just add the user to the existing list
      vSet(usersWhoReactedByWishIdAndReactionValue.value, emojiKey, {
        ...emojiData,
        users: [reactionUser, ...emojiData.users], // the most recent reaction is added to the front of the list
      })
    }
  }

  const updateUsersWhoReactedFromRealtime = async (payload: RealtimeReactionPayload): Promise<void> => {
    const { wishId, userHashid, totalReactionCountChange, reactionType } = payload
    if (reactionType !== ReactionType.Emoji) {
      // For non-emoji reactions, we don't need to update the users who reacted cache
      // since the values are not used in the UI yet.
      return
    }

    const post = surfacePostsStore.postEntitiesById[wishId]
    if (post?.id == null) return

    let contributor: User
    try {
      const user = await surfaceUserContributorsStore.getUserByHashId(userHashid, surfaceStore.wallId)
      if (user == null) return
      contributor = user
    } catch (error) {
      captureFetchException(error, { source: 'updateUsersWhoReactedFromRealtime' })
      return
    }

    const reactionUser = createReactionUser(contributor)
    const { emojiCode, oldEmojiCode, newEmojiCode } = payload as RealtimeEmojiReactionPayload
    const isRealtimeEmojiCreatePayload = emojiCode != null && totalReactionCountChange > 0
    const isRealtimeEmojiDeletePayload = emojiCode != null && totalReactionCountChange < 0
    const isRealtimeEmojiUpdatePayload = oldEmojiCode != null && newEmojiCode != null && oldEmojiCode !== newEmojiCode

    if (isRealtimeEmojiUpdatePayload) {
      const oldEmojiKey = createReactionUserKey(post.id, oldEmojiCode)
      const newEmojiKey = createReactionUserKey(post.id, newEmojiCode)

      removeUserFromEmoji(oldEmojiKey, userHashid)
      addUserToEmoji(newEmojiKey, reactionUser)
    } else if (isRealtimeEmojiCreatePayload) {
      const emojiKey = createReactionUserKey(post.id, emojiCode)
      addUserToEmoji(emojiKey, reactionUser)
    } else if (isRealtimeEmojiDeletePayload) {
      const emojiKey = createReactionUserKey(post.id, emojiCode)
      removeUserFromEmoji(emojiKey, userHashid)
    }
  }

  const clearUsersWhoReacted = (): void => {
    usersWhoReactedByWishIdAndReactionValue.value = {}
    isLoadingUsersWhoReactedByIdAndReactionValue.value = {}
  }
  // #endregion

  const resetReactions = (): void => {
    accumulatedReactionsByWishId.value = {}
    clearUsersWhoReacted()
  }

  return {
    // for delayed reaction sorting
    shouldDeferSortingPostsByReactions,
    accumulatedReactionsForDelayedSorting,
    startTrackingPostForDelayedReactionSort,
    stopTrackingPostForDelayedReactionSort,

    // state
    isUpdatingReaction,
    accumulatedReactionsByWishId,
    postIdBeingReactedTo,
    reactionDropdownLeft,
    reactionDropdownTop,
    reactionDropdownWidth,
    reactionIdsByPost,
    usersWhoReactedByWishIdAndReactionValue,
    isLoadingUsersWhoReactedByIdAndReactionValue,
    // getters
    hasReactions,
    originalReactionData,
    currentReactionData,
    currentReactionType,
    allowedEmojis,
    shouldShowVoteStyleButton,
    voteStyleEmojiCode,
    accumulatedReactionsTotalSumAverage,
    accumulatedReactionsConfidenceNumber,

    // action
    fetchAccumulatedReactions,
    updateAccumulatedReactions,
    refreshAccumulatedReactions,
    updateUserReaction,
    createReaction,
    updateReaction,
    deleteReaction,
    startEditingReaction,
    stopEditingReaction,
    resetReactions,
    fetchUsersWhoReacted,
    fetchMoreUsersWhoReacted,
    getUsersWhoReacted,
    getIsLoadingUsers,
    updateUsersWhoReactedFromRealtime,
    clearUsersWhoReacted,
  }
})
